import {useMemo, useState} from 'react';
import {ChevronRight, ChevronDown, Lock, MailWarning} from 'lucide-react';
import {
  Badge,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Input
} from '@kroll-resolver-crisp/ui-components-react';
import {type Classification} from '@/api/classifications';
import {cn} from '@/lib/utils';
import {isPolicyViolationType, isVisibleForFacet} from '@/lib/classificationType';

type Props = {
  readonly classifications: Classification[];
  readonly onSelect: (vertical: Classification, risk: Classification) => void;
  readonly selectedRiskId?: string | undefined;
  // Multi-select: every currently-picked leaf. Additive alongside selectedRiskId, which the
  // single-select correction flows (QaReviewModal) still pass - when both are absent nothing is
  // highlighted, and when this is present it wins.
  readonly selectedRiskIds?: readonly string[] | undefined;
  // Which taxonomy to show when classifying a submission. Omit for facet-agnostic correction flows.
  // A node with children expands when clicked; only a leaf risk is selectable.
  readonly category?: 'Actor' | 'Content';
};

export function ClassificationTree({
  classifications,
  onSelect,
  selectedRiskId,
  selectedRiskIds,
  category
}: Props) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const {visibleNodes, childrenByParent} = useMemo(() => {
    const childrenByParent = new Map<string, Classification[]>();
    for (const classification of classifications) {
      if (!classification.parentId) {
        continue;
      }

      const children = childrenByParent.get(classification.parentId) ?? [];
      children.push(classification);
      childrenByParent.set(classification.parentId, children);
    }

    const visibleNodes = new Set<string>();
    const matchesQuery = (classification: Classification) =>
      !query || classification.displayName.toLowerCase().includes(query.toLowerCase());
    const visit = (classification: Classification, ancestorMatchesQuery: boolean): boolean => {
      if (!isVisibleForFacet(classification, category)) {
        return false;
      }

      const nodeMatchesQuery = matchesQuery(classification);
      // Mapped rather than `.some()` so every visible child is visited and recorded in
      // `visibleNodes` for rendering.
      const childVisibility = (childrenByParent.get(classification.id) ?? []).map((child) =>
        visit(child, ancestorMatchesQuery || nodeMatchesQuery)
      );
      const visible =
        childVisibility.length > 0
          ? childVisibility.includes(true)
          : !isPolicyViolationType(classification.type) &&
            (ancestorMatchesQuery || nodeMatchesQuery);
      if (visible) {
        visibleNodes.add(classification.id);
      }

      return visible;
    };

    for (const classification of classifications.filter((c) => isPolicyViolationType(c.type))) {
      visit(classification, false);
    }

    return {visibleNodes, childrenByParent};
  }, [classifications, category, query]);

  const roots = useMemo(
    () =>
      classifications.filter(
        (classification) =>
          isPolicyViolationType(classification.type) && visibleNodes.has(classification.id)
      ),
    [classifications, visibleNodes]
  );

  const isSelected = (id: string) =>
    selectedRiskIds ? selectedRiskIds.includes(id) : selectedRiskId === id;

  const renderNode = (node: Classification, root: Classification, depth: number) => {
    const children = (childrenByParent.get(node.id) ?? []).filter((child) =>
      visibleNodes.has(child.id)
    );
    const isOpen = expanded.has(node.id) || query !== '';
    const isRoot = node.id === root.id;

    return (
      <div key={node.id}>
        <div className="flex items-center" style={{paddingLeft: `${depth * 1.5}rem`}}>
          {children.length > 0 ? (
            <button
              type="button"
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.displayName}`}
              className="hover:bg-muted flex w-9 shrink-0 justify-center py-2"
              onClick={() => {
                toggle(node.id);
              }}>
              {isOpen ? (
                <ChevronDown className="text-muted-foreground size-3.5" />
              ) : (
                <ChevronRight className="text-muted-foreground size-3.5" />
              )}
            </button>
          ) : (
            <span className="w-9 shrink-0" />
          )}
          <button
            type="button"
            className={cn(
              'hover:bg-accent flex min-w-0 flex-1 items-center gap-2 py-2 pr-3 pl-1 text-left text-xs transition-colors',
              isRoot && 'font-medium',
              isSelected(node.id) && 'bg-primary/10 text-primary-ink font-medium'
            )}
            // Only meaningful in multi-select, where a leaf click toggles rather than advances.
            aria-pressed={
              selectedRiskIds && children.length === 0 ? isSelected(node.id) : undefined
            }
            onClick={() => {
              if (children.length > 0) {
                toggle(node.id);
              } else {
                onSelect(root, node);
              }
            }}>
            {node.displayName}
            {node.restricted ? <Lock className="text-muted-foreground ml-auto size-3" /> : null}
            {!isRoot && node.isPotentialP0 ? (
              <Badge variant="destructive" className="ml-auto">
                <MailWarning />
                Potential P0
              </Badge>
            ) : null}
          </button>
        </div>
        {isOpen && children.length > 0 ? (
          <div className="bg-muted/20 divide-y">
            {children.map((child) => renderNode(child, root, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const toggle = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  return (
    <div className="space-y-2">
      <Input
        placeholder="Search classifications…"
        value={query}
        className="max-w-xs"
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      <div className="border-border max-h-96 divide-y overflow-y-auto rounded-lg border">
        {roots.map((root) => renderNode(root, root, 0))}
        {roots.length === 0 ? (
          <Empty className="py-6">
            <EmptyHeader>
              <EmptyTitle>No classifications found</EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : null}
      </div>
    </div>
  );
}
